import { ActionIconProps, ComboboxItem, Group, GroupProps, Input, MantineSize, Stack, StackProps } from '@mantine/core';
import { useDidUpdate, useUncontrolled } from '@mantine/hooks';
import { RemoveRowButton } from '@vision/ui/components/RowActions/RemoveRowButton';
import { RowActions } from '@vision/ui/components/RowActions/RowActions';
import { UseLazyLoadSelectOptions } from '@vision/ui/hooks/useLazyLoadSelect';
import { KeyValue } from '@vision/ui/interfaces/key-value';
import { ensureArray } from '@vision/ui/utils/array.utils';
import React, { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KeyValuePairInputPropsType,
  KeyValuePairInputSwitchCase,
  KeyValuePairInputType,
  KeyValuePairInputValueType,
} from './components/KeyValuePairInputSwitchCase/KeyValuePairInputSwitchCase';
import {
  KeyValuePairInputAddButton,
  KeyValuePairInputAddButtonProps,
} from './components/KeyValuePairInputAddButton/KeyValuePairInputAddButton';

export interface KeyValuePairInputProps {
  // Accessible names; the defaults are generic. Inputs get theirs only without a visible label.
  addButtonLabel?: string;
  getKeyAriaLabel?: (number: number) => string;
  getRemoveButtonLabel?: (number: number) => string;
  getValueAriaLabel?: (number: number) => string;
  disabled?: boolean;
  inputSize?: MantineSize;
  buttonSize?: MantineSize;
  keyError?: (index: number) => React.ReactNode;
  keyInputType?: KeyValuePairInputType;
  keyItemOptions?: ComboboxItem[];
  keyLabel?: string;
  keyPlaceholder?: string;
  keyProps?: KeyValuePairInputPropsType;
  keyValueInputMapping?: {
    keySelectedValue: string;
    lazyLoadOptions?: UseLazyLoadSelectOptions<any, any>;
    valueInputType?: KeyValuePairInputType;
    valueItemOptions?: ComboboxItem[];
  }[];
  label?: string;
  maxPairs?: number;
  multiple?: boolean;
  name: string;
  onChange: (value: KeyValue<KeyValuePairInputValueType, KeyValuePairInputValueType>[]) => void;
  value: KeyValue<KeyValuePairInputValueType, KeyValuePairInputValueType>[];
  valueError?: (index: number) => React.ReactNode;
  valueInputType?: KeyValuePairInputType;
  valueItemOptions?: ComboboxItem[];
  valueLabel?: string;
  valuePlaceholder?: string;
  valueProps?: KeyValuePairInputPropsType;
  enterKeyChangeClearValue?: boolean;
  addButtonProps?: ActionIconProps;
  removeButtonProps?: ActionIconProps;
  containerProps?: StackProps;
  keyValuePairProps?: GroupProps;
  leftIcon?: React.ReactNode;
  renderAddButton?: (props: KeyValuePairInputAddButtonProps) => React.ReactNode;
}

export interface KeyValuePairInputRef {
  clear: VoidFunction;
}

export const KeyValuePairInput = forwardRef(
  (
    {
      addButtonLabel,
      getKeyAriaLabel,
      getRemoveButtonLabel,
      getValueAriaLabel,
      disabled = false,
      inputSize = 'sm',
      buttonSize = inputSize,
      keyError,
      keyInputType = 'input',
      keyItemOptions = [],
      keyLabel,
      keyPlaceholder,
      keyProps,
      keyValueInputMapping,
      label,
      maxPairs = 10,
      multiple,
      name,
      onChange,
      value,
      valueError,
      valueInputType = 'input',
      valueItemOptions = [],
      valueLabel,
      valuePlaceholder,
      valueProps,
      enterKeyChangeClearValue = false,
      addButtonProps,
      removeButtonProps,
      containerProps,
      keyValuePairProps,
      leftIcon,
      renderAddButton,
    }: KeyValuePairInputProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const hasKeyLabel = Boolean(keyLabel || keyProps?.label);
    const hasValueLabel = Boolean(valueLabel || valueProps?.label);
    const addLabel = addButtonLabel ?? t('keyValuePair.add');
    const [pairs, setPairs] = useUncontrolled<KeyValue<KeyValuePairInputValueType, KeyValuePairInputValueType>[]>({
      value,
      defaultValue: [{ key: '', value: '' }],
      onChange,
    });

    const handleAddPair = () => {
      setPairs([...pairs, { key: '', value: '' }]);
    };

    const handleRemovePair = (index: number) => {
      setPairs(pairs.filter((_, i) => i !== index));
    };

    const handleChange = (
      index: number,
      value: KeyValuePairInputValueType,
      key: KeyValuePairInputValueType,
      clearValue = false,
    ) => {
      setPairs(
        pairs.map((pair, i) => {
          if (i === index) {
            return {
              key,
              value: clearValue ? '' : value,
            };
          }

          return pair;
        }),
      );
    };

    useDidUpdate(() => {
      setPairs([pairs[0]]);
    }, [multiple]);

    useImperativeHandle(ref, () => ({
      clear: () => setPairs([{ key: '', value: '' }]),
    }));

    return (
      <>
        {label && <Input.Label>{label}</Input.Label>}
        <Stack gap={10} {...containerProps}>
          {pairs.map((pair, index) => {
            const found = ensureArray(keyValueInputMapping).find((item) => item.keySelectedValue === pair.key);
            const computedValueInputType = found?.valueInputType || valueInputType;
            const computedValueItemOptions = found?.valueItemOptions || valueItemOptions;
            const number = index + 1;
            // An add button is disabled with the input, as are the inputs and the remove button.
            const isAddDisabled =
              disabled || !pairs[pairs.length - 1].key || !pairs[pairs.length - 1].value || pairs.length === maxPairs;

            return (
              <Group
                key={index}
                wrap="nowrap"
                gap={10}
                align={keyProps?.error || valueProps?.error ? 'center' : 'flex-end'}
                data-testid={`key-value-pair-${index}`}
                {...keyValuePairProps}
              >
                <Group gap={10} align="flex-start" wrap="nowrap">
                  {leftIcon}
                  <KeyValuePairInputSwitchCase
                    label={keyLabel}
                    placeholder={keyPlaceholder ?? t('key')}
                    inputType={keyInputType}
                    inputProps={
                      hasKeyLabel
                        ? { size: inputSize, ...keyProps }
                        : {
                            size: inputSize,
                            ...keyProps,
                            'aria-label': getKeyAriaLabel?.(number) ?? t('keyValuePair.key', { number }),
                          }
                    }
                    itemOptions={keyItemOptions}
                    name={name}
                    part="key"
                    index={index}
                    disabled={disabled}
                    handleChange={(index, value) => handleChange(index, pair.value, value, enterKeyChangeClearValue)}
                    value={pair.key}
                    error={keyError?.(index)}
                  />

                  <KeyValuePairInputSwitchCase
                    label={valueLabel}
                    placeholder={valuePlaceholder ?? t('value')}
                    inputType={computedValueInputType}
                    inputProps={
                      hasValueLabel
                        ? { size: inputSize, ...valueProps }
                        : {
                            size: inputSize,
                            ...valueProps,
                            'aria-label': getValueAriaLabel?.(number) ?? t('keyValuePair.value', { number }),
                          }
                    }
                    itemOptions={computedValueItemOptions}
                    name={name}
                    part="value"
                    index={index}
                    disabled={disabled}
                    handleChange={(index, value) => handleChange(index, value, pair.key)}
                    value={pair.value}
                    lazyLoadOptions={found?.lazyLoadOptions}
                    error={valueError?.(index)}
                  />
                </Group>

                {multiple && (
                  <RowActions
                    buttonSize={buttonSize}
                    addAction={
                      index === pairs.length - 1 &&
                      (renderAddButton ? (
                        renderAddButton({
                          disabled: isAddDisabled,
                          handleAddPair,
                          addButtonProps: { size: `input-${buttonSize}`, ...addButtonProps },
                          label: addLabel,
                        })
                      ) : (
                        <KeyValuePairInputAddButton
                          disabled={isAddDisabled}
                          handleAddPair={handleAddPair}
                          addButtonProps={{ size: `input-${buttonSize}`, ...addButtonProps }}
                          label={addLabel}
                        />
                      ))
                    }
                    removeAction={
                      pairs.length > 1 && (
                        <RemoveRowButton
                          label={getRemoveButtonLabel?.(number) ?? t('keyValuePair.remove', { number })}
                          data-testid={`button-remove-key-value-pair-${index + 1}`}
                          disabled={disabled}
                          onClick={() => handleRemovePair(index)}
                          size={`input-${buttonSize}`}
                          {...removeButtonProps}
                        />
                      )
                    }
                  />
                )}
              </Group>
            );
          })}
        </Stack>
      </>
    );
  },
);
